// W13-1b — THE ASK, DRIVEN. HER RULING: IT APPEARS AUTOMATICALLY ON THE FIRST SIGNED-IN OPEN OF THE
// INSTALLED APP. No bell-hunting, no extra step.
//
// **THE SPLIT, AND IT IS DELIBERATE.** The DECISION (`shouldOfferPush`) and the SHEET
// (`PushOptInSheet`) are both hookless and are both RUN/CALLED by `scripts/w13-push.mjs`. This file
// is the only part that holds state, and it holds it so App.tsx does not — the whole feature costs
// App.tsx two lines (an import and a mount), which is R23's HomeShowcase precedent.
//
// **WHY THIS IS A COMPONENT AND NOT AN EFFECT IN APP.TSX:** the ask has four conditions and three
// async steps, and the reason it was reported missing in the first place is that nothing could see
// it. In its own file the law beside it is testable; inside a 13,000-line file it would be another
// effect nobody can reach.
import { useCallback, useEffect, useRef, useState } from "react";
import { PushOptInSheet } from "./PushOptInSheet";
import {
  PUSH_ANSWERS, isInstalledApp, pushSupported, shouldOfferPush,
  subscriptionPayload, urlBase64ToUint8Array,
} from "../pushOptIn.js";
import { HEAL, healDecision, shouldRunHeal } from "../pushHeal.js";
// ITEM 7(a) — INSTALL, THEN NOTIFICATIONS, ONE WARM MOTION. The install prompt announces on its own
// success and this is the listening end; the DECISION is the shared law's, called at both ends.
import { INSTALL_ANSWERS, INSTALL_HANDOFF, installHandoff, watchInstalled } from "../installPrompt.js";

const post = (url: string, body: unknown) =>
  fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) })
    .then((r) => r.json()).catch(() => null);

export function PushOptIn({ signedIn, hasRoom }: { signedIn: boolean; hasRoom: boolean }) {
  const [open, setOpen] = useState(false);
  const keyRef = useRef("");
  // One offer per app launch, whatever re-renders happen. Without this, any change to `hasRoom`
  // re-runs the effect and the sheet can reappear after she has answered it in this same session.
  const askedThisSession = useRef(false);

  // ITEM 7(a) — **ONE ROUTINE, TWO MOMENTS.** The launch effect and the just-installed handoff both
  // run THIS, so P3-2B's law (the reasons are REPORTED, refusals included) holds on the new moment by
  // EXISTING rather than by being remembered — and there is no second answer to *may we ask her*
  // (R28/R29). The only thing that differs between the two callers is the one fact that really
  // differs: whether she installed the app a second ago.
  const offerAsk = useCallback(async (justInstalled: boolean, alive: () => boolean) => {
    if (askedThisSession.current) return;
    // P3-2B — **TINA'S CASE FORCED THIS: THE ASK USED TO EARLY-RETURN BEFORE SAYING ANYTHING.**
    // Four conditions gate the offer, and when one of them failed the member simply saw nothing and
    // the server learned nothing — so "no ask appeared" was three different bugs wearing one face.
    // The conditions are now MEASURED FIRST and REPORTED, whatever they say. It is one row per
    // launch per member, which is the cheapest possible price for never guessing at this again.
    //
    // Note it no longer early-returns on `hasRoom` or `installed` either: those were the two
    // suspects her case pointed at, and a condition that silences the diagnostic that would prove
    // it is the exact shape this house keeps paying for.
    const installed = isInstalledApp(window);
    const supported = pushSupported(window);
    const r = await fetch("/api/push/key").then((x) => x.json()).catch(() => null);
    if (!alive()) return;
    const decision = shouldOfferPush({
      installed,
      signedIn: true,
      hasRoom,
      priorAnswer: r?.answered || null,
      permission: window.Notification?.permission || "default",
      supported,
      justInstalled,
    });
    // The reasons were always computed and always thrown away. They are the receipt.
    await post("/api/push/ask-check", {
      offered: Boolean(decision.offer && r?.ok && r?.configured && r?.key),
      reasons: decision.reasons,
      installed, supported, hasRoom,
      standaloneMedia: typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)").matches : false,
      navigatorStandalone: (window.navigator as { standalone?: boolean }).standalone === true,
      permission: window.Notification?.permission || "default",
      answered: r?.answered || "",
      configured: Boolean(r?.configured),
    });
    // `configured: false` means no VAPID keys, and the honest response is silence: offering a
    // moment that could not complete is worse than not offering it.
    if (!alive() || !r?.ok || !r.configured || !r.key) return;
    if (!decision.offer) return;
    keyRef.current = r.key;
    askedThisSession.current = true;
    setOpen(true);
  }, [hasRoom]);

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    void offerAsk(false, () => alive);
    return () => { alive = false; };
  }, [signedIn, offerAsk]);

  // ITEM 7(a) — **HER WARM MOTION'S SECOND HALF.** The install prompt announces on its own success
  // and this catches it, in the SAME session and the same tab. `installHandoff` decides what follows
  // — and it is asked with the fact only this end has: whether web push exists in THIS context. On
  // Android after the browser's own install it does, and the ask lands seconds after she tapped ADD
  // IT; on an iOS Safari tab it does not, so the law answers WAIT and the ask keeps its existing home
  // on the first open of the installed app rather than being fired into a browser that cannot carry
  // it (R18: *not now* is not *never*).
  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    const stop = watchInstalled(window, () => {
      if (installHandoff({ answer: INSTALL_ANSWERS.INSTALLED, pushHere: pushSupported(window) }) !== INSTALL_HANDOFF.ASK) return;
      void offerAsk(true, () => alive);
    });
    return () => { alive = false; stop(); };
  }, [signedIn, offerAsk]);

  // P3-2 — **THE REINSTALL HEAL, ON EVERY LAUNCH OF THE INSTALLED APP.** Michaela's case: a
  // reinstall destroys the browser's push subscription, the account still says "granted", and
  // `shouldOfferPush` therefore never offers again — so the state proving she said yes is the state
  // preventing her from ever being asked again. Nothing on her screen says so. This asks the BROWSER
  // rather than trusting the stored answer, and it runs before (and independently of) the ask above.
  useEffect(() => {
    if (!signedIn || !hasRoom) return;
    if (!shouldRunHeal({ installed: isInstalledApp(window), signedIn: true, supported: pushSupported(window) })) return;
    let alive = true;
    (async () => {
      const r = await fetch("/api/push/key").then((x) => x.json()).catch(() => null);
      if (!alive || !r?.ok || !r.configured || !r.key) return;
      let live = null;
      try {
        const reg = await navigator.serviceWorker.ready;
        live = await reg.pushManager.getSubscription();
      } catch { live = null; }
      if (!alive) return;
      const decision = healDecision({
        answered: r.answered || null,
        hasLiveSubscription: Boolean(live),
        permission: window.Notification?.permission || "default",
        installed: true,
        supported: true,
        // DT-2a: the one fact the device cannot know — the house's beacon evidence that this
        // endpoint is accepted and dead. It rides on the key route the heal already fetched.
        houseCause: r.houseCause || "",
      });
      // HER ORDER: the verdict is reported EVERY launch, including the skips, so a silent phone can
      // never again be diagnosed by inference from a changed column.
      const report = (why: string) => post("/api/push/heal", {
        verdict: decision, why,
        hasLiveSubscription: Boolean(live),
        permission: window.Notification?.permission || "default",
        answered: r.answered || "",
        installed: true,
      });
      if (decision === HEAL.NOTHING) { await report(`answered=${r.answered || "none"}`); return; }
      if (decision === HEAL.REASK) {
        // **THE GRANT IS STALE AND THE PERMISSION IS GONE — so the field is CLEARED, not set to
        // "later".** Her device proved why: `shouldOfferPush` refuses to offer on ANY prior answer,
        // `later` included, so the first version moved her from one permanently silent state into
        // another and the ask could never return. She has not answered for THIS installation.
        await report("stale grant, permission revoked (iOS deletes the permission with the app)");
        // AND THE ASK RETURNS IN THIS SAME LAUNCH. Making her open the app twice to see a prompt she
        // never dismissed is the defect one step smaller.
        if (alive) { keyRef.current = r.key; askedThisSession.current = true; setOpen(true); }
        return;
      }
      try {
        let sub = live;
        if (decision === HEAL.RESUBSCRIBE) {
          // **NO PROMPT AND NO GESTURE.** iOS often keeps the notification permission across a
          // reinstall, and where it does she must never be asked again for something she already
          // agreed to — the app simply mints the endpoint it lost.
          const reg = await navigator.serviceWorker.ready;
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(r.key),
          });
        }
        const payload = subscriptionPayload(sub);
        // **THE TWO RE-MINTS ARE REPORTED APART** (P3-2 round two: the reasons are REPORTED, refusals
        // included). A mint after a reinstall and a mint because the HOUSE proved the endpoint dead
        // are the same act for two different reasons, and the receipt is the only place the second
        // one is ever visible — DT-1's whole argument, arriving on the repair rather than the send.
        await report(
          decision === HEAL.RESUBSCRIBE
            ? (r.houseCause
              ? `re-minted silently: the house has beacon evidence this endpoint is dead (${r.houseCause})`
              : "re-minted silently, iOS kept the permission")
            : "live subscription re-posted",
        );
        // Posting a LIVE subscription again is deliberate and cheap: the route is idempotent on the
        // endpoint, so it heals a server row that was swept while the browser still held the grant.
        if (payload) await post("/api/push/subscribe", payload);
      } catch {
        // A push service having a bad minute is not a reason to change what her account says.
      }
    })();
    return () => { alive = false; };
  }, [signedIn, hasRoom]);

  // HER TAP IS THE GESTURE THAT RAISES THE BROWSER PROMPT. This runs inside the click handler on
  // purpose: `Notification.requestPermission()` is gesture-gated, and an await BEFORE it can cost
  // the gesture on some browsers, so nothing slow happens above it.
  const accept = async () => {
    setOpen(false);
    let permission = "default";
    try { permission = await window.Notification.requestPermission(); } catch { permission = "default"; }
    if (permission !== "granted") {
      // A DENIAL IS RECORDED AS A DENIAL, and it is permanent: the browser will not let the app ask
      // again. Anything else (dismissed without choosing) is "later", which is not a refusal.
      await post("/api/push/answer", { answer: permission === "denied" ? PUSH_ANSWERS.DENIED : PUSH_ANSWERS.LATER });
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        // Required by every browser: a push that shows nothing is not allowed, and asking for one
        // is how a subscription gets refused outright.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRef.current),
      });
      const payload = subscriptionPayload(sub);
      if (payload) await post("/api/push/subscribe", payload);
    } catch {
      // The permission is granted but the subscribe failed (an offline launch, a push service having
      // a bad minute). Recorded as "later" rather than granted, so the moment can return rather than
      // leaving her opted in to something that has no endpoint behind it.
      await post("/api/push/answer", { answer: PUSH_ANSWERS.LATER });
    }
  };

  const later = async () => {
    setOpen(false);
    await post("/api/push/answer", { answer: PUSH_ANSWERS.LATER });
  };

  return <PushOptInSheet open={open} onAccept={accept} onLater={later} />;
}
